import { useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/browser';
import { useQueryClient } from '@tanstack/react-query';

import usePollSingularityJob from '@/hooks/usePollSingularityJob';
import queryKeys from '@/queries/queryKeys';

type Params = {
  entitiesJobId: Maybe<string>;
  emailId: Maybe<string>;
  messageId: Maybe<string>;
  onComplete?: () => void;
};

export default function usePollAnalysisJobs({
  entitiesJobId,
  emailId,
  messageId,
  onComplete,
}: Params) {
  const queryClient = useQueryClient();
  const { executeJob } = usePollSingularityJob();

  const [isPolling, setIsPolling] = useState(false);

  const activeJobIdRef = useRef<string | null>(null);

  const executeJobRef = useRef(executeJob);
  executeJobRef.current = executeJob;

  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!entitiesJobId || !emailId || !messageId) return;

    activeJobIdRef.current = entitiesJobId;
    setIsPolling(true);

    let cancelled = false;

    const isStillActive = () =>
      !cancelled && activeJobIdRef.current === entitiesJobId;

    (async () => {
      try {
        const entitiesJob = await executeJobRef.current<{
          actions_job_id: string;
        }>(entitiesJobId);

        if (!isStillActive()) return;

        queryClient.removeQueries({
          queryKey: queryKeys.onesurance.recommendation(emailId),
        });

        await queryClient.invalidateQueries({
          queryKey: queryKeys.emailAnalysis.byMessageId(messageId),
        });

        if (!isStillActive()) return;

        await executeJobRef.current(entitiesJob.result.actions_job_id);

        if (!isStillActive()) return;

        await queryClient.invalidateQueries({
          queryKey: queryKeys.emailAnalysis.byMessageId(messageId),
        });
      } catch (error) {
        if (!isStillActive()) return;
        Sentry.captureException(error);
      }

      if (!isStillActive()) return;
      setIsPolling(false);
      onCompleteRef.current?.();
    })();

    return () => {
      cancelled = true;
    };
  }, [entitiesJobId, emailId, messageId, queryClient]);

  return { isPolling };
}
